/**
 * useAnalyzeApi - Platform-Aware Analysis Operations
 *
 * Routes analysis operations (AI detection, SIC, sentence analysis) to:
 * - Electron: Local statistical analysis or Ollama-based
 * - Web/Cloud: HTTP API to Workers AI
 *
 * @module @humanizer/studio/hooks/useAnalyzeApi
 */

import { useCallback, useMemo, useState, useEffect } from 'react';
import { usePlatformRouter, type Backend } from './usePlatformRouter';
import type { PlatformContextValue } from '../contexts/PlatformContext';
import type {
  DetectionResult, SicResult, SentenceSessionInfo, SentenceStepResult,
  GPTZeroDetectionResult, GptzeroAvailability, SicProgressUpdate,
  SentencingV2Result, NoemaGraphResult, NoemaGraphDiffResult,
  NoemaProgressUpdate, ChapterGraphResult, ChapterGraphProgress,
} from '../types/analysis';

// Re-export cloud types for compatibility
export type { DetectionResult, SicResult, SentenceSessionInfo, SentenceStepResult, GPTZeroDetectionResult, GptzeroAvailability, SentencingV2Result, NoemaGraphResult, NoemaGraphDiffResult, NoemaProgressUpdate, ChapterGraphResult, ChapterGraphProgress };

export interface AnalyzeApi {
  isAvailable: boolean;
  backend: Backend;
  unavailableReason: string | null;
  hasLLMAnalysis: boolean;

  detect: (text: string) => Promise<DetectionResult>;
  detectGptzero: (text: string) => Promise<GPTZeroDetectionResult>;
  gptzeroAvailability: GptzeroAvailability;
  refreshGptzeroAvailability: () => void;

  sic: (text: string, genreHint?: string, onProgress?: (progress: SicProgressUpdate) => void) => Promise<SicResult>;
  sentenceStart: (text: string) => Promise<SentenceSessionInfo>;
  sentenceStep: (sessionId: string) => Promise<SentenceStepResult>;
  sentencingV2: (text: string, options?: { title?: string; mode?: 'fiction' | 'nonfiction' | 'mixed' }) => Promise<SentencingV2Result>;
  noemaGraph: (text: string, options?: { title?: string; mode?: string; useSentencingHints?: boolean }, onProgress?: (p: NoemaProgressUpdate) => void) => Promise<NoemaGraphResult>;
  noemaGraphDiff: (sourceText: string, targetText: string, options?: { title?: string; mode?: string; useSentencingHints?: boolean }) => Promise<NoemaGraphDiffResult>;
  chapterGraph: (chapters: Array<{ index: number; title: string; draftBufferId: string }>, onProgress?: (p: ChapterGraphProgress) => void) => Promise<ChapterGraphResult>;
}

export function useAnalyzeApi(): AnalyzeApi {
  const { getBackend, isAvailable, backend, unavailableReason, cloudApi, capabilitiesRef } =
    usePlatformRouter<{ hasLocalLLM: boolean }>({
      resolveCapabilities(platform: PlatformContextValue | null, isElectron: boolean) {
        if (platform) {
          return {
            canUseLocal: platform.isElectron,
            canUseCloud: platform.canUseCloudLLM,
            hasLocalLLM: platform.canUseLocalLLM,
            preferredBackend: platform.isElectron ? 'local' as const : (platform.canUseCloudLLM ? 'cloud' as const : 'none' as const),
          };
        }
        return {
          canUseLocal: isElectron,
          canUseCloud: !isElectron,
          hasLocalLLM: false,
          preferredBackend: isElectron ? 'local' as const : 'cloud' as const,
        };
      },
      resolveAvailability(platform: PlatformContextValue | null, isElectron: boolean) {
        const isAvailable = isElectron || (platform?.canUseCloudLLM ?? !isElectron);
        const backend = isElectron ? 'local' as const : (platform?.canUseCloudLLM ? 'cloud' as const : 'none' as const);
        const unavailableReason = isAvailable ? null : 'Sign in to use cloud analysis.';
        return { isAvailable, backend, unavailableReason };
      },
      platformDeps: (platform) => [platform?.isElectron, platform?.canUseLocalLLM, platform?.canUseCloudLLM],
    });

  const hasLLMAnalysis = capabilitiesRef.current.hasLocalLLM || capabilitiesRef.current.canUseCloud;

  // ── GPTZero availability tracking ──────────────────────────────────
  const [gptzeroAvail, setGptzeroAvail] = useState<GptzeroAvailability>({
    available: false,
    source: 'none',
    enabled: false,
    proxyEnabled: false,
    wordsUsed: 0,
    wordLimit: 0,
    wordsRemaining: 0,
    callCount: 0,
  });

  const refreshGptzeroAvailability = useCallback(() => {
    const currentBackend = getBackend();
    if (currentBackend === 'local' && window.electronAnalyze?.gptzeroAvailability) {
      window.electronAnalyze.gptzeroAvailability().then(setGptzeroAvail).catch(() => {});
    } else if (currentBackend === 'cloud') {
      // Cloud availability: assume available if user is authenticated (tier check happens server-side)
      setGptzeroAvail({ available: true, source: 'cloud', enabled: true, proxyEnabled: true, wordsUsed: 0, wordLimit: 0, wordsRemaining: 0, callCount: 0 });
    }
  }, [getBackend]);

  useEffect(() => {
    refreshGptzeroAvailability();
    // Refresh when BYOK keys change
    const handler = () => refreshGptzeroAvailability();
    window.addEventListener('byok-keys-changed', handler);
    return () => window.removeEventListener('byok-keys-changed', handler);
  }, [refreshGptzeroAvailability]);

  // ── GPTZero detection ──────────────────────────────────────────────
  const detectGptzero = useCallback(async (text: string): Promise<GPTZeroDetectionResult> => {
    const currentBackend = getBackend();

    // Pre-call quota gate (local backend checks in handler too, but this avoids the round-trip)
    if (currentBackend === 'local' && gptzeroAvail.wordLimit > 0) {
      const wordCount = text.trim().split(/\s+/).length;
      const pctUsed = (gptzeroAvail.wordsUsed / gptzeroAvail.wordLimit) * 100;

      if (pctUsed >= 100) {
        throw new Error(
          `GPTZero monthly word limit reached (${gptzeroAvail.wordsUsed.toLocaleString()} / ${gptzeroAvail.wordLimit.toLocaleString()} words). Increase your limit in Settings > Usage.`
        );
      }

      if (pctUsed >= 90) {
        const remaining = gptzeroAvail.wordLimit - gptzeroAvail.wordsUsed;
        console.warn(
          `[GPTZero] Quota warning: ${remaining.toLocaleString()} words remaining (${Math.round(pctUsed)}% used). This call uses ~${wordCount.toLocaleString()} words.`
        );
      }
    }

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.detectGptzero) {
        const result = await electronAnalyze.detectGptzero(text);
        // Refresh availability to update quota counters
        refreshGptzeroAvailability();
        return result as GPTZeroDetectionResult;
      }
      throw new Error('GPTZero detection not available locally');
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.analyze.detectGptzero(text);
      // Refresh availability to update quota counters
      refreshGptzeroAvailability();
      return result as GPTZeroDetectionResult;
    }

    throw new Error(unavailableReason || 'No analysis backend available');
  }, [getBackend, unavailableReason, cloudApi, refreshGptzeroAvailability, gptzeroAvail]);

  const detect = useCallback(async (text: string): Promise<DetectionResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.detect) {
        const r = await electronAnalyze.detect(text);
        return {
          aiLikelihood: r.aiLikelihood,
          confidence: r.confidence,
          verdict: r.verdict,
          features: {
            // Lexical
            ttrWindowed: r.features.ttrWindowed,
            hapaxLegomenaRate: r.features.hapaxLegomenaRate,
            contractionDensity: r.features.contractionDensity,
            ngramDiversity: r.features.ngramDiversity ?? 0,
            // Structural
            burstiness: r.features.burstiness ?? 0,
            sentenceComplexity: r.features.sentenceComplexity,
            sentenceLengthKurtosis: r.features.sentenceLengthKurtosis,
            // Content
            tellPhraseScore: r.features.tellPhraseScore ?? 0,
            punctuationSignals: r.features.punctuationSignals,
            punctuationContrast: r.features.punctuationContrast,
            compressionRatio: r.features.compressionRatio,
            // Embedding
            embeddingTrajectory: r.features.embeddingTrajectory,
          },
          tellPhrases: r.tellPhrases ?? { score: 0, matches: [], aiTellWeight: 0, humanTellWeight: 0 },
          sentenceAnalysis: r.sentenceAnalysis,
          humanizationRecommendations: r.humanizationRecommendations ?? [],
          processingTimeMs: r.processingTimeMs ?? 0,
          detectorVersion: r.detectorVersion ?? 'local-3.0',
          method: r.method as 'statistical' | 'statistical+embedding' | 'llm-enhanced',
          embeddingTrajectory: r.embeddingTrajectory,
        };
      }
      // Fallback: return minimal detection result
      return {
        aiLikelihood: 0.5,
        confidence: 'low',
        verdict: 'mixed',
        features: { burstiness: 0, semicolonRate: 0, emDashRate: 0, tellPhraseScore: 0, ngramDiversity: 0 },
        tellPhrases: { score: 0, matches: [], aiTellWeight: 0, humanTellWeight: 0 },
        humanizationRecommendations: [],
        processingTimeMs: 0,
        detectorVersion: 'local-1.0',
        method: 'statistical',
      };
    }

    if (currentBackend === 'cloud') {
      return cloudApi.analyze.detect(text);
    }

    throw new Error(unavailableReason || 'No analysis backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const sic = useCallback(async (text: string, genreHint?: string, onProgress?: (progress: SicProgressUpdate) => void): Promise<SicResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.sic) {
        // Subscribe to progress events if callback provided
        let unsubscribe: (() => void) | undefined;
        if (onProgress && electronAnalyze.onSicProgress) {
          unsubscribe = electronAnalyze.onSicProgress(onProgress);
        }

        try {
          const r = await electronAnalyze.sic(text, genreHint);
          return {
            version: 'sic.v1',
            sicScore: r.sicScore,
            genre: r.genre || genreHint || 'unknown',
            features: r.features,
            inflectionPoints: r.inflectionPoints,
            notes: r.notes,
            llmCallCount: r.llmCallCount,
            processingTimeMs: r.processingTimeMs,
          };
        } finally {
          unsubscribe?.();
        }
      }
      throw new Error('SIC analysis requires Ollama. Start Ollama to enable.');
    }

    if (currentBackend === 'cloud') {
      return cloudApi.analyze.sic(text, genreHint);
    }

    throw new Error(unavailableReason || 'No analysis backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const sentenceStart = useCallback(async (text: string): Promise<SentenceSessionInfo> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.sentenceStart) {
        const r = await electronAnalyze.sentenceStart(text);
        return {
          sessionId: r.sessionId,
          totalSentences: r.totalSentences,
          currentIndex: r.currentIndex,
        };
      }
      throw new Error('Sentence analysis requires Ollama. Start Ollama to enable.');
    }

    if (currentBackend === 'cloud') {
      return cloudApi.analyze.sentenceStart(text);
    }

    throw new Error(unavailableReason || 'No analysis backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const sentenceStep = useCallback(async (sessionId: string): Promise<SentenceStepResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.sentenceStep) {
        const r = await electronAnalyze.sentenceStep(sessionId);
        return {
          complete: r.complete,
          step: r.step ? {
            sentenceIndex: r.step.sentenceIndex,
            sentence: r.step.sentence,
            measurement: {
              reading: r.step.measurement.reading,
              isValid: r.step.measurement.isValid,
              probSum: r.step.measurement.probSum,
            },
            rhoDistance: r.step.rhoDistance,
          } : null,
        };
      }
      throw new Error('Sentence analysis requires Ollama. Start Ollama to enable.');
    }

    if (currentBackend === 'cloud') {
      return cloudApi.analyze.sentenceStep(sessionId);
    }

    throw new Error(unavailableReason || 'No analysis backend available');
  }, [getBackend, unavailableReason, cloudApi]);

  const sentencingV2 = useCallback(async (
    text: string,
    options?: { title?: string; mode?: 'fiction' | 'nonfiction' | 'mixed' }
  ): Promise<SentencingV2Result> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.sentencingV2) {
        const r = await electronAnalyze.sentencingV2(text, options);
        return r as SentencingV2Result;
      }
      throw new Error('Sentencing v2 requires Ollama embeddings. Start Ollama to enable.');
    }

    // Cloud not yet supported for v2
    throw new Error('Sentencing v2 is currently available only on Electron (local analysis).');
  }, [getBackend]);

  const noemaGraph = useCallback(async (
    text: string,
    options?: { title?: string; mode?: string; useSentencingHints?: boolean },
    onProgress?: (p: NoemaProgressUpdate) => void
  ): Promise<NoemaGraphResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.noemaGraph) {
        // Subscribe to progress events if callback provided
        let unsubscribe: (() => void) | undefined;
        if (onProgress && electronAnalyze.onNoemaGraphProgress) {
          unsubscribe = electronAnalyze.onNoemaGraphProgress(onProgress);
        }

        try {
          const r = await electronAnalyze.noemaGraph(text, options);
          return r as NoemaGraphResult;
        } finally {
          unsubscribe?.();
        }
      }
      throw new Error('Noema graph requires LLM + Ollama embeddings. Start Ollama to enable.');
    }

    throw new Error('Noema graph is currently available only on Electron (local analysis).');
  }, [getBackend]);

  const noemaGraphDiff = useCallback(async (
    sourceText: string,
    targetText: string,
    options?: { title?: string; mode?: string; useSentencingHints?: boolean }
  ): Promise<NoemaGraphDiffResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.noemaGraphDiff) {
        const r = await electronAnalyze.noemaGraphDiff(sourceText, targetText, options);
        return r as NoemaGraphDiffResult;
      }
      throw new Error('Noema graph diff requires LLM + Ollama embeddings. Start Ollama to enable.');
    }

    throw new Error('Noema graph diff is currently available only on Electron (local analysis).');
  }, [getBackend]);

  const chapterGraph = useCallback(async (
    chapters: Array<{ index: number; title: string; draftBufferId: string }>,
    onProgress?: (p: ChapterGraphProgress) => void
  ): Promise<ChapterGraphResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronAnalyze = window.electronAnalyze;
      if (electronAnalyze?.chapterGraph) {
        let unsubscribe: (() => void) | undefined;
        if (onProgress && electronAnalyze.onChapterGraphProgress) {
          unsubscribe = electronAnalyze.onChapterGraphProgress(onProgress);
        }

        try {
          const r = await electronAnalyze.chapterGraph(chapters);
          return r as ChapterGraphResult;
        } finally {
          unsubscribe?.();
        }
      }
      throw new Error('Chapter graph requires LLM + Ollama embeddings. Start Ollama to enable.');
    }

    throw new Error('Chapter graph is currently available only on Electron (local analysis).');
  }, [getBackend]);

  return useMemo(() => ({
    isAvailable,
    backend,
    unavailableReason,
    hasLLMAnalysis,
    detect,
    detectGptzero,
    gptzeroAvailability: gptzeroAvail,
    refreshGptzeroAvailability,
    sic,
    sentenceStart,
    sentenceStep,
    sentencingV2,
    noemaGraph,
    noemaGraphDiff,
    chapterGraph,
  }), [isAvailable, backend, unavailableReason, hasLLMAnalysis, detect, detectGptzero, gptzeroAvail, refreshGptzeroAvailability, sic, sentenceStart, sentenceStep, sentencingV2, noemaGraph, noemaGraphDiff, chapterGraph]);
}

export default useAnalyzeApi;
